import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Link2, Check } from "lucide-react";
import { FaPinterestP, FaFacebookF, FaXTwitter } from "react-icons/fa6";
import { api, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";

const SHARE_BASE = "https://beautytradeclub.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
  }, [slug]);

  usePageMeta(
    post && post.title ? `${post.title} | Beauty Trade Club Blog` : "Beauty Trade Club Blog",
    post && post.excerpt ? post.excerpt : "",
    `/blog/${slug}`
  );

  if (post === null) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#E8956B]" /></div>;
  if (post === false) return <div className="text-center py-32 text-neutral-400" data-testid="blog-not-found">Post not found.</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#CE744A] hover:underline mb-6" data-testid="blog-back-link">
        <ArrowLeft size={15} /> All posts
      </Link>
      <p className="text-xs text-neutral-400 font-semibold uppercase tracking-wider">{post.date}</p>
      <h1 className="font-display text-4xl font-black text-neutral-900 mt-2 leading-tight" data-testid="blog-post-title">{post.title}</h1>
      {post.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {post.tags.map((t) => (
            <span key={t} className="text-[11px] font-semibold text-[#CE744A] bg-[#E8956B]/10 border border-[#E8956B]/30 rounded-full px-2.5 py-1">#{t}</span>
          ))}
        </div>
      )}
      {post.image_url && (
        <img src={imgUrl(post.image_url)} alt={post.title} className="w-full rounded-2xl mt-6 aspect-[16/9] object-cover" />
      )}
      <ShareRow post={post} slug={slug} copied={copied} setCopied={setCopied} />
      <div
        className="blog-content mt-8"
        data-testid="blog-post-content"
        dangerouslySetInnerHTML={{ __html: post.content_html }}
      />
      <div className="mt-12 rounded-2xl bg-[#E8956B]/10 border border-[#E8956B]/30 p-6 text-center">
        <h3 className="font-display text-2xl font-bold text-neutral-900">Ready to trade your beauty stash?</h3>
        <p className="text-neutral-600 mt-1">Buy, sell and swap new & gently-used makeup — safely, with escrow protection.</p>
        <Link to="/shop" className="inline-block mt-4 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-7 py-3 transition-all" data-testid="blog-cta-shop">
          Explore the Shop
        </Link>
      </div>
    </article>
  );
}

function ShareRow({ post, slug, copied, setCopied }) {
  const url = `${SHARE_BASE}/blog/${slug}`;
  const text = post.title;
  const media = post.image_url ? `${SHARE_BASE}${post.image_url}` : "";
  const links = {
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(media)}&description=${encodeURIComponent(text)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  };
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied! 🔗"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy link"); }
  };
  const btn = "w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110";
  return (
    <div className="flex items-center gap-3 mt-6 border-y border-neutral-200 py-4" data-testid="blog-share-row">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-400 mr-1">Share</span>
      <button data-testid="share-pinterest" onClick={() => open(links.pinterest)} aria-label="Share on Pinterest" className={`${btn} bg-[#E60023]`}><FaPinterestP size={16} /></button>
      <button data-testid="share-facebook" onClick={() => open(links.facebook)} aria-label="Share on Facebook" className={`${btn} bg-[#1877F2]`}><FaFacebookF size={16} /></button>
      <button data-testid="share-x" onClick={() => open(links.x)} aria-label="Share on X" className={`${btn} bg-neutral-900`}><FaXTwitter size={16} /></button>
      <button data-testid="share-copy" onClick={copy} aria-label="Copy link" className={`${btn} bg-[#E8956B]`}>{copied ? <Check size={16} /> : <Link2 size={16} />}</button>
    </div>
  );
}
